import { useAppSelector } from '@saaga/shared/store/store'
import { useCallback, useMemo, useState } from 'react'

import type { ChatInputMode } from '@/components/chat/chat-input-mode.type'
import type { ChatItem } from '@/components/chat/chat-message.interface'

import { useDraftAgencyChatActions } from './use-draft-agency-chat-actions.hook'
import { useDraftAgencyChatHandlers } from './use-draft-agency-chat-handlers.hook'
import { useDraftAgencyChatMessages } from './use-draft-agency-chat-messages.hook'

export interface DraftAgencyPreviewChatState {
  itemList: ChatItem[]
  inputValue: string
  inputMode: ChatInputMode
  isLoading: boolean
  isOpen: boolean
  panelWidth: number
}

export interface DraftAgencyPreviewChatHandlers {
  setInputValue: (value: string) => void
  setInputMode: (mode: ChatInputMode) => void
  handleSend: () => void
  handleReset: () => void
  handleOpen: () => void
  handleClose: () => void
  handleToggle: () => void
  handleResize: (deltaX: number) => void
}

export interface UseDraftAgencyPreviewChatReturn {
  state: DraftAgencyPreviewChatState
  handlers: DraftAgencyPreviewChatHandlers
  conversationId: string | null
}

export interface UseDraftAgencyPreviewChatProps {
  agencyId: string
  agencySlug?: string
}

export const useDraftAgencyPreviewChat = ({
  agencyId,
  agencySlug,
}: UseDraftAgencyPreviewChatProps): UseDraftAgencyPreviewChatReturn => {
  const { isOpen, panelWidth, draftConversationId } = useAppSelector((s) => s.draftAgencyChat)
  const panelHandlers = useDraftAgencyChatHandlers({ agencyId })
  const messages = useDraftAgencyChatMessages(agencySlug)

  const [inputValue, setInputValue] = useState('')
  const [inputMode, setInputMode] = useState<ChatInputMode>('message')
  const onInputClear = useCallback(() => setInputValue(''), [])

  const { handleSend, handleReset } = useDraftAgencyChatActions({
    inputValue,
    inputMode,
    agencySlug,
    messages,
    onInputClear,
  })

  const state = useMemo<DraftAgencyPreviewChatState>(
    () => ({
      itemList: messages.itemList,
      inputValue,
      inputMode,
      isLoading: messages.isLoading || messages.isSending,
      isOpen,
      panelWidth,
    }),
    [
      messages.itemList,
      inputValue,
      inputMode,
      messages.isLoading,
      messages.isSending,
      isOpen,
      panelWidth,
    ]
  )

  const handlers = useMemo<DraftAgencyPreviewChatHandlers>(
    () => ({ setInputValue, setInputMode, handleSend, handleReset, ...panelHandlers }),
    [handleSend, handleReset, panelHandlers]
  )

  return { state, handlers, conversationId: draftConversationId }
}
